import { formatStatus, formatTimestamp } from '../constants/workflowLabels';
import { getPermitMapPosition } from './mapCoordinates';
import { getPermitDisplayFields } from './permitFields';

function escapeHtml(s) {
  return String(s ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function printHtmlDocument(title, bodyHtml) {
  const win = window.open('', '_blank', 'noopener,noreferrer,width=800,height=900');
  if (!win) {
    alert('Allow pop-ups to print or save as PDF.');
    return;
  }
  win.document.write(`<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8" />
  <title>${escapeHtml(title)}</title>
  <style>
    body { font-family: Georgia, 'Times New Roman', serif; margin: 1.25in; color: #1a2e44; line-height: 1.5; }
    h1 { font-size: 1.35rem; margin: 0 0 0.25rem; }
    .brand { font-size: 0.75rem; letter-spacing: 0.08em; text-transform: uppercase; color: #5a6b7d; }
    .meta { font-size: 0.85rem; color: #4a5f73; margin-bottom: 1.5rem; }
    table { width: 100%; border-collapse: collapse; margin: 1rem 0; font-size: 0.9rem; }
    th, td { border: 1px solid #ccc; padding: 0.4rem 0.6rem; text-align: left; }
    th { background: #f0f3f6; }
    .footer { margin-top: 2rem; font-size: 0.75rem; color: #7f8c8d; }
    @media print { body { margin: 0.75in; } }
  </style>
</head>
<body>
${bodyHtml}
<p class="footer">RobinhoodSpace False Alarm Permit Software · GovOps operational record · ${escapeHtml(new Date().toLocaleString())}</p>
<script>window.onload = function() { window.print(); };</script>
</body>
</html>`);
  win.document.close();
}

/** Printable false-alarm permit notice (Save as PDF from browser print dialog). */
export function printPermitNotice(permit) {
  if (!permit) return;
  const pos = getPermitMapPosition(permit);
  const extraRows = getPermitDisplayFields(permit)
    .map(([label, value]) => `<tr><th>${escapeHtml(label)}</th><td>${escapeHtml(value)}</td></tr>`)
    .join('');
  const body = `
    <p class="brand">RobinhoodSpace · False alarm permit notice</p>
    <h1>Permit notice</h1>
    <p class="meta">Permit ID: ${escapeHtml(permit.id)} · Status: ${escapeHtml(formatStatus(permit.status))}</p>
    <table>
      <tr><th>Type</th><td>${escapeHtml(permit.type)}</td></tr>
      <tr><th>Description</th><td>${escapeHtml(permit.description)}</td></tr>
      <tr><th>Reference #</th><td>${escapeHtml(permit.permitNumber || '—')}</td></tr>
      <tr><th>Coordinates</th><td>${pos ? `${pos.lat}, ${pos.lng}` : '—'}</td></tr>
      <tr><th>Submitted</th><td>${escapeHtml(formatTimestamp(permit.createdAt || permit.date))}</td></tr>
      ${extraRows}
    </table>
    <p>This document is generated from the RobinhoodSpace GovOps demo for operational review. It is not a legal notice of violation unless issued by your jurisdiction.</p>
  `;
  printHtmlDocument(`Permit notice ${permit.id}`, body);
}

/** Printable case lifecycle report with timeline events. */
export function printCaseReport(caseRow, events = []) {
  if (!caseRow) return;
  const rows = events
    .map(
      (ev) => `<tr>
        <td>${escapeHtml(formatTimestamp(ev.ts))}</td>
        <td>${escapeHtml(ev.kind || '—')}</td>
        <td>${escapeHtml(ev.label)}</td>
        <td>${escapeHtml(ev.detail)}</td>
      </tr>`
    )
    .join('');
  const body = `
    <p class="brand">RobinhoodSpace · Case operational report</p>
    <h1>${escapeHtml(caseRow.title || 'False alarm case')}</h1>
    <p class="meta">Case ID: ${escapeHtml(caseRow.id)} · Status: ${escapeHtml(formatStatus(caseRow.status))} · ${escapeHtml(caseRow.city || '')}</p>
    <table>
      <tr><th>Opened</th><td>${escapeHtml(formatTimestamp(caseRow.createdAt))}</td></tr>
      <tr><th>Last updated</th><td>${escapeHtml(formatTimestamp(caseRow.updatedAt))}</td></tr>
    </table>
    <h2 style="font-size:1rem;margin-top:1.5rem;">Audit &amp; lifecycle timeline</h2>
    <table>
      <thead><tr><th>When</th><th>Kind</th><th>Event</th><th>Detail</th></tr></thead>
      <tbody>${rows || '<tr><td colspan="4">No events recorded.</td></tr>'}</tbody>
    </table>
  `;
  printHtmlDocument(`Case report ${caseRow.id}`, body);
}
